import React, { useState } from 'react';
import {
  Activity,
  FolderOpen,
  KeyRound,
  Lock,
  Server,
} from 'lucide-react';
import { OpsPasswordGate } from './components';
import type { OpsEnvironment, OpsRuntime } from './opsConsoleData';
import {
  isOpsUnlocked,
  lockOpsConsole,
  readEnv,
  readRuntime,
  unlockOpsConsole,
  writeEnv,
  writeRuntime,
} from './opsConsoleStorage';
import ServicesPanel from './ServicesPanel';
import PathsPanel from './PathsPanel';
import SecretsPanel from './SecretsPanel';

type TabId = 'services' | 'paths' | 'secrets';

const TABS: { id: TabId; label: string; icon: React.ComponentType<{ size?: number; className?: string }> }[] = [
  { id: 'services', label: 'Services', icon: Server },
  { id: 'paths', label: 'Paths / URLs', icon: FolderOpen },
  { id: 'secrets', label: 'Secrets', icon: KeyRound },
];

const ENVS: OpsEnvironment[] = ['dev', 'staging', 'production'];
const RUNTIMES: OpsRuntime[] = ['no-docker', 'docker', 'k8s'];

export default function OpsConsolePage() {
  const [unlocked, setUnlocked] = useState(() => isOpsUnlocked());
  const [env, setEnv] = useState<OpsEnvironment>(() => readEnv());
  const [runtime, setRuntime] = useState<OpsRuntime>(() => readRuntime());
  const [tab, setTab] = useState<TabId>('services');

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-slate-100">
        <OpsPasswordGate
          checkPassword={(password) => unlockOpsConsole(password)}
          onSuccess={() => setUnlocked(true)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-2.5 min-w-0">
            <span className="mt-0.5 inline-flex p-1.5 rounded-md bg-indigo-50 text-indigo-600">
              <Activity size={16} />
            </span>
            <div className="min-w-0">
              <h1 className="text-base font-semibold text-slate-900">Local Stack Ops Console</h1>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Profile{' '}
                <span className="font-mono text-slate-700">
                  {env} · {runtime}
                </span>
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
              Environment
              <select
                value={env}
                onChange={(e) => {
                  const next = e.target.value as OpsEnvironment;
                  setEnv(next);
                  writeEnv(next);
                }}
                className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 outline-none focus:border-indigo-400"
              >
                {ENVS.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </select>
            </label>

            <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
              Runtime
              <select
                value={runtime}
                onChange={(e) => {
                  const next = e.target.value as OpsRuntime;
                  setRuntime(next);
                  writeRuntime(next);
                }}
                className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 outline-none focus:border-indigo-400"
              >
                {RUNTIMES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>

            <button
              type="button"
              onClick={() => {
                lockOpsConsole();
                setUnlocked(false);
              }}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              <Lock size={12} /> Lock
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
          Synthetic control plane — local UI only; does not start real processes.
        </div>

        <div className="flex flex-wrap gap-1 border-b border-slate-200 pb-px">
          {TABS.map(({ id, label, icon: Icon }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                  active
                    ? 'border-indigo-600 text-indigo-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Icon size={13} />
                {label}
              </button>
            );
          })}
        </div>

        {tab === 'services' && <ServicesPanel env={env} runtime={runtime} />}
        {tab === 'paths' && <PathsPanel env={env} runtime={runtime} />}
        {tab === 'secrets' && <SecretsPanel env={env} />}
      </main>
    </div>
  );
}
